import axios, { AxiosHeaders, type InternalAxiosRequestConfig, type AxiosError } from "axios";
import { authService } from "../auth/service";
import { shouldInvalidateFromResponse } from "../auth/session";

type RetriableConfig = InternalAxiosRequestConfig & { _retry?: boolean };

export const httpClient = axios.create({
  baseURL: process.env.API_URL,
  timeout: 30000,
});

function setAuthHeader(config: InternalAxiosRequestConfig, token: string) {
  const headers =
    config.headers instanceof AxiosHeaders
      ? config.headers
      : new AxiosHeaders(config.headers || {});
  headers.set("Authorization", `Bearer ${token}`);
  config.headers = headers;
}

async function showLoginTaskpane() {
  try {
    localStorage.setItem("navigationTarget", "login");

    if (typeof Office !== "undefined" && Office.addin?.showAsTaskpane) {
      await Office.addin.showAsTaskpane();
    }
  } catch (err) {
    console.warn("Unable to show task pane programmatically:", err);
  }
}

httpClient.interceptors.request.use(async (config) => {
  const token = await authService.getStoredToken();
  if (token) setAuthHeader(config, token);
  return config;
});

httpClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const original = error.config as RetriableConfig | undefined;
    const status = error.response?.status;

    if (!original) {
      return Promise.reject(error);
    }

    if (shouldInvalidateFromResponse(status) && !original._retry) {
      original._retry = true;

      try {
        await authService.invalidate();
        const newToken = await authService.refresh();
        setAuthHeader(original, newToken);
        return httpClient(original);
      } catch (refreshError) {
        await authService.logout();
        await showLoginTaskpane();
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);